var _dec, _dec2, _class, _Item;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { omitProps, getElementType, callRenderProp, withDeterministicId } from '@instructure/ui-react-utils';
import { withStyle } from '@instructure/emotion';
import generateStyles from "./styles.js";
import generateComponentTheme from "./theme.js";
import { allowedProps } from "./props.js";
/**
---
parent: Options
id: Options.Item
---
**/
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
let Item = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyles, generateComponentTheme), _dec(_class = _dec2(_class = (_Item = class Item extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this._descriptionId = void 0;
    this._descriptionId = props.deterministicId('OptionsItem-description');
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }
  renderContent(renderLabel, contentVariant) {
    const _this$props3 = this.props,
      styles = _this$props3.styles,
      variant = _this$props3.variant,
      as = _this$props3.as,
      role = _this$props3.role,
      children = _this$props3.children;
    return _jsx("span", {
      css: [styles === null || styles === void 0 ? void 0 : styles.content, contentVariant],
      role: "presentation",
      "aria-hidden": "true",
      children: callRenderProp(renderLabel, {
        variant,
        as,
        role,
        children
      })
    });
  }
  render() {
    const _this$props4 = this.props,
      as = _this$props4.as,
      href = _this$props4.href,
      role = _this$props4.role,
      styles = _this$props4.styles,
      description = _this$props4.description,
      descriptionRole = _this$props4.descriptionRole,
      renderBeforeLabel = _this$props4.renderBeforeLabel,
      renderAfterLabel = _this$props4.renderAfterLabel,
      elementRef = _this$props4.elementRef,
      children = _this$props4.children,
      voiceoverRoleBugWorkaround = _this$props4.voiceoverRoleBugWorkaround;
    const ElementType = getElementType(Item, this.props, () => as);
    const InnerElementType = href ? 'a' : 'span';
    const passthroughProps = omitProps(this.props, Item.allowedProps);
    const childrenContent = callRenderProp(children);
    const descriptionContent = callRenderProp(description);
    const ariaDescribedBy = this.props['aria-describedby'] || (descriptionContent ? this._descriptionId : void 0);
    return _jsxs(ElementType, {
      role: voiceoverRoleBugWorkaround ? role : 'none',
      "data-cid": "Options.Item",
      css: styles === null || styles === void 0 ? void 0 : styles.item,
      ref: element => {
        this.ref = element;
        if (typeof elementRef === 'function') {
          elementRef(element);
        }
      },
      "aria-describedby": voiceoverRoleBugWorkaround ? ariaDescribedBy : void 0,
      children: [_jsxs(InnerElementType, {
        ...passthroughProps,
        css: styles === null || styles === void 0 ? void 0 : styles.container,
        role: href || voiceoverRoleBugWorkaround ? void 0 : role,
        href: href,
        "aria-describedby": voiceoverRoleBugWorkaround ? void 0 : ariaDescribedBy,
        children: [childrenContent, descriptionContent && _jsx("span", {
          css: styles === null || styles === void 0 ? void 0 : styles.description,
          role: descriptionRole,
          id: this._descriptionId,
          children: descriptionContent
        })]
      }), renderBeforeLabel && this.renderContent(renderBeforeLabel, styles === null || styles === void 0 ? void 0 : styles.contentBefore), renderAfterLabel && this.renderContent(renderAfterLabel, styles === null || styles === void 0 ? void 0 : styles.contentAfter)]
    });
  }
}, _Item.displayName = "Item", _Item.componentId = 'Options.Item', _Item.allowedProps = allowedProps, _Item.defaultProps = {
  as: 'span',
  variant: 'default',
  role: 'listitem',
  voiceoverRoleBugWorkaround: false,
  beforeLabelContentVAlign: 'center',
  afterLabelContentVAlign: 'center',
  isSelected: false
}, _Item)) || _class) || _class);
export default Item;
export { Item };